import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  VStack,
  Badge,
  Heading,
  Button,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Text,
  useColorModeValue,
  IconButton,
  HStack,
  Tag,
  TagLabel,
  TagCloseButton,
  useToast,
  Spinner,
  Wrap,
  WrapItem,
  Progress,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Alert,
  AlertIcon,
  Flex,
  Stepper,
  Step,
  StepIndicator,
  StepTitle,
  StepSeparator,
  StepStatus,
  Circle,
} from '@chakra-ui/react';
import {
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TimeIcon,
} from '@chakra-ui/icons';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from "@google/generative-ai";
import { AlphavantageDataService } from '../services/alphavantageData';
import { CandleChart } from '../components/CandleChart';
import { AdvisorHistory } from '../types';
import { Link } from 'react-router-dom';
import { FaRobot, FaExchangeAlt } from 'react-icons/fa'; // Import exchange icon for data flow
import { FaBrain, FaChartLine, FaCheck, FaCogs } from 'react-icons/fa';
import { MdTimeline } from 'react-icons/md';

// Add these interfaces
interface AnalysisProgress {
  message: string;
  progress: number;
}

interface FormattedAnalysis {
  analysis: string;
  stockData: any[];
}

// Replace existing interfaces with simplified ones
interface AnalysisResult {
  analysis: string;
}

interface StockChartData {
  symbol: string;
  data: any[];
}

const StockChartCard: React.FC<{ stockData: StockChartData }> = ({ stockData }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <Card
      bg={bgColor}
      borderRadius="lg"
      boxShadow="md"
      border="1px"
      borderColor={borderColor}
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
    >
      <CardHeader pb={2}>
        <Heading size="md" color={useColorModeValue('purple.600', 'purple.300')}>
          {stockData.symbol} Price History
        </Heading>
      </CardHeader>
      <CardBody pt={4} px={6}>
        <CandleChart
          data={stockData.data}
          isLoading={false}
          selectedInterval="month"
          onIntervalChange={() => {}}
        />
      </CardBody>
    </Card>
  );
};

const AnalysisResultView: React.FC<{ data: string }> = ({ data }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  
  // Split content into sections and ensure we have valid markdown sections
  const sections = data
    .split(/(?=# )/g)  // Split on headers
    .filter(section => section.trim().startsWith('# ')) // Ensure we only get valid sections
    .slice(1); // Remove the first section
  
  return (
    <SimpleGrid 
      columns={{ base: 1, lg: 2 }} 
      spacing={6}
      sx={{
        '& > *:last-child:nth-child(odd)': {
          gridColumn: { lg: '1 / -1' }
        }
      }}
    >
      {sections.map((section, index) => {
        const [title, ...content] = section.split('\n');
        return (
          <Card 
            key={index}
            bg={bgColor}
            borderRadius="lg"
            boxShadow="md"
            border="1px"
            borderColor={borderColor}
            transition="all 0.2s"
            _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
          >
            <CardHeader pb={2}>
              <Heading size="md" color={useColorModeValue('purple.600', 'purple.300')}>
                {title.replace(/^#\s+/, '')}
              </Heading>
            </CardHeader>
            <CardBody pt={4} px={6}>
              <Box
                sx={{
                  // ...existing styles...
                  '& h2': {
                    fontSize: 'lg',
                    fontWeight: 'semibold',
                    mb: 4,
                    mt: 5,
                    color: useColorModeValue('blue.600', 'blue.300')
                  },
                  '& h3': {
                    fontSize: 'md',
                    fontWeight: 'medium',
                    mb: 3,
                    mt: 4
                  },
                  '& p': {
                    mb: 4,
                    lineHeight: 1.7,
                    px: 2
                  },
                  '& ul': {
                    mb: 4,
                    pl: 8,
                    pr: 4
                  },
                  '& li': {
                    mb: 3,
                    pl: 2
                  },
                  // ...rest of existing styles...
                }}
              >
                <ReactMarkdown>{content.join('\n')}</ReactMarkdown>
              </Box>
            </CardBody>
          </Card>
        );
      })}
    </SimpleGrid>
  );
};

// Add this near the top of the file, after imports
const API_BASE_URL = 'https://finomo-middleware.ml-meastro.workers.dev';

// Update the AgentWorkflow component to accept progress as a prop
const AgentWorkflow: React.FC<{ progress: AnalysisProgress | null }> = ({ progress }) => {
  const agentColor = useColorModeValue('blue.500', 'purple.300');
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  
  return (
    <Box 
      w="full" 
      maxW="4xl" 
      mx="auto" 
      p={8} 
      bg={bgColor} 
      borderRadius="xl"
      position="relative"
    >
      <SimpleGrid columns={3} spacing={8} mb={8}>
        {/* Market Analysis Agent */}
        <VStack>
          <motion.div
            animate={{
              y: [0, -10, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Box textAlign="center">
              <FaRobot size={40} color={agentColor} />
              <Text mt={2} fontWeight="bold">Market Analyzer</Text>
              <Text fontSize="sm" color="gray.500">Processing market data...</Text>
            </Box>
          </motion.div>
        </VStack>

        {/* Financial Analysis Agent */}
        <VStack>
          <motion.div
            animate={{
              y: [0, -10, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 2,
              delay: 0.6,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Box textAlign="center">
              <FaRobot size={40} color={agentColor} />
              <Text mt={2} fontWeight="bold">Financial Analyzer</Text>
              <Text fontSize="sm" color="gray.500">Analyzing metrics...</Text>
            </Box>
          </motion.div>
        </VStack>

        {/* Strategy Agent */}
        <VStack>
          <motion.div
            animate={{
              y: [0, -10, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 2,
              delay: 1.2,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Box textAlign="center">
              <FaRobot size={40} color={agentColor} />
              <Text mt={2} fontWeight="bold">Strategy Builder</Text>
              <Text fontSize="sm" color="gray.500">Formulating strategy...</Text>
            </Box>
          </motion.div>
        </VStack>
      </SimpleGrid>

      {/* Data Flow Lines */}
      <Box position="absolute" top="50%" left="0" right="0" zIndex={0}>
        <motion.svg
          width="100%"
          height="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <line
            x1="0"
            y1="0"
            x2="100%"
            y2="0"
            stroke={agentColor}
            strokeWidth="2"
            strokeDasharray="4 4"
          />
        </motion.svg>
      </Box>

      {/* Analysis Output */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        <VStack spacing={4} pt={8} position="relative" zIndex={1}>
          <Box
            p={4}
            bg={useColorModeValue('white', 'gray.800')}
            borderRadius="lg"
            boxShadow="lg"
            w="full"
          >
            <HStack spacing={4}>
              <Box flex={1}>
                <motion.div
                  animate={{
                    scaleX: [0, 1],
                    opacity: [0.5, 1],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                  }}
                >
                  <Progress
                    value={progress?.progress || 0}
                    size="sm"
                    colorScheme="blue"
                    hasStripe
                    isAnimated
                  />
                </motion.div>
              </Box>
              <Text fontWeight="medium">
                {progress?.message || 'Synthesizing analysis...'}
              </Text>
            </HStack>
          </Box>
        </VStack>
      </motion.div>
    </Box>
  );
};

// Add this new component for the timeline steps
const TimelineStep: React.FC<{
  step: number;
  currentStep: number;
  title: string;
  icon: React.ReactElement;
  isCompleted: boolean;
}> = ({ step, currentStep, title, icon, isCompleted }) => {
  const isActive = step === currentStep;
  const isPast = step < currentStep;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: step * 0.2 }}
    >
      <Box
        position="relative"
        mb={8}
      >
        <Flex align="center" mb={2}>
          <Circle
            size="40px"
            bg={isPast ? 'green.500' : isActive ? 'blue.500' : 'gray.200'}
            color={isPast || isActive ? 'white' : 'gray.500'}
            transition="all 0.3s"
            transform={isActive ? 'scale(1.2)' : 'scale(1)'}
            boxShadow={isActive ? 'lg' : 'none'}
          >
            {isPast ? <FaCheck /> : icon}
          </Circle>
          <Box
            flex="1"
            h="2px"
            bg={isPast ? 'green.500' : 'gray.200'}
            ml={2}
            transition="all 0.3s"
          />
        </Flex>
        <Text
          color={isActive ? 'blue.500' : 'gray.500'}
          fontWeight={isActive ? 'bold' : 'normal'}
        >
          {title}
        </Text>
      </Box>
    </motion.div>
  );
};

// Add this new component for the analysis agents
const AnalysisAgents: React.FC<{ progress: AnalysisProgress | null }> = ({ progress }) => {
  const agents = [
    { name: 'Market Analyzer', icon: FaChartLine, color: 'blue.400' },
    { name: 'Data Processor', icon: FaCogs, color: 'purple.400' },
    { name: 'Strategy AI', icon: FaBrain, color: 'green.400' }
  ];

  return (
    <Box position="relative" h="300px">
      {/* Connection Lines */}
      <Box
        position="absolute"
        top="50%"
        left="0"
        right="0"
        height="2px"
        bg="gray.200"
        zIndex={0}
      >
        <motion.div
          style={{
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, #4299E1 0%, #9F7AEA 50%, #48BB78 100%)',
            transformOrigin: 'left',
          }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: progress ? progress.progress / 100 : 0 }}
          transition={{ duration: 2 }}
        />
      </Box>

      {/* Agents */}
      <Flex justify="space-between" position="relative" zIndex={1}>
        {agents.map((agent, index) => (
          <motion.div
            key={agent.name}
            initial={{ y: 50, opacity: 0 }}
            animate={{ 
              y: 0, 
              opacity: 1,
              scale: [1, 1.1, 1],
            }}
            transition={{
              delay: index * 0.3,
              duration: 1,
              scale: {
                repeat: Infinity,
                duration: 2,
                repeatType: "reverse"
              }
            }}
          >
            <VStack
              bg={useColorModeValue('white', 'gray.800')}
              p={4}
              borderRadius="xl"
              boxShadow="xl"
              spacing={3}
              w="180px"
            >
              <Circle
                size="50px"
                bg={agent.color}
                color="white"
              >
                <agent.icon size="24px" />
              </Circle>
              <Text fontWeight="bold">{agent.name}</Text>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                {index === 0 ? 'Analyzing market data...' :
                 index === 1 ? 'Processing metrics...' :
                 'Formulating strategy...'}
              </Text>
              {/* Pulse Animation */}
              <Box position="relative">
                <Circle
                  size="10px"
                  bg={agent.color}
                  position="absolute"
                  top="-5px"
                  left="50%"
                  transform="translateX(-50%)"
                >
                  <motion.div
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      border: `2px solid ${agent.color}`,
                      position: 'absolute',
                    }}
                    animate={{
                      scale: [1, 2, 1],
                      opacity: [1, 0, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.3,
                    }}
                  />
                </Circle>
              </Box>
            </VStack>
          </motion.div>
        ))}
      </Flex>
    </Box>
  );
};

export const AdvisorAgent: React.FC = () => {
  const [currentStepState, setCurrentStepState] = useState(1);
  const [stocks, setStocks] = useState<string[]>([]);
  const [currentStock, setCurrentStock] = useState('');
  const [capital, setCapital] = useState<number>(0);
  const [duration, setDuration] = useState<number>(1);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showInputs, setShowInputs] = useState(true);
  const [result, setResult] = useState<string | null>(null);
  const [progress, setProgress] = useState<AnalysisProgress | null>(null);
  const toast = useToast();
  const [formattedResult, setFormattedResult] = useState<FormattedAnalysis | null>(null);
  const [stockCharts, setStockCharts] = useState<StockChartData[]>([]);
  const [apiError, setApiError] = useState<string | null>(null); // Add this state

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Add Gemini AI configuration
  const geminiConfig = {
    temperature: 1,
    topP: 0.95,
    topK: 40,
    maxOutputTokens: 8192,
    responseMimeType: "text/plain",
  };

  const handleAddStock = () => {
    if (currentStock && !stocks.includes(currentStock)) {
      if (stocks.length < 5) {
        setStocks([...stocks, currentStock.toUpperCase()]);
        setCurrentStock('');
        if (stocks.length === 4) {
          // Automatically move to next step if max stocks reached
          setTimeout(() => setCurrentStepState(2), 500);
        }
      }
    }
  };

  const handleRemoveStock = (stockToRemove: string) => {
    setStocks(stocks.filter(stock => stock !== stockToRemove));
  };

  const handleNext = () => {
    if (currentStepState < 3 && stocks.length > 0) {
      setCurrentStepState(currentStepState + 1);
    }
  };

  const fetchStockData = async (symbols: string[]) => {
    const alphavantage = new AlphavantageDataService();
    const chartDataPromises = symbols.map(async (symbol) => {
      try {
        const data = await alphavantage.getMonthlyAdjusted(symbol);
        return {
          symbol,
          data: data.map(d => ({
            timestamp: d.timestamp,
            open: d.open,
            high: d.high,
            low: d.low,
            close: d.close,
            volume: d.volume,
            oi: 0
          }))
        };
      } catch (error) {
        console.error(`Error fetching data for ${symbol}:`, error);
        return null;
      }
    });

    const results = await Promise.all(chartDataPromises);
    return results.filter((result): result is StockChartData => result !== null);
  };

  const handleSubmit = async () => {
    if (stocks.length === 0) {
      toast({
        title: 'Error',
        description: 'Please add at least one stock',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsAnalyzing(true);
    setShowInputs(false);
    setApiError(null); // Reset error state

    try {
      // Fetch stock data first
      const chartData = await fetchStockData(stocks);
      setStockCharts(chartData);

      const genAI = new GoogleGenerativeAI('AIzaSyDTC-uqSepuy07K7-qoM5_Q4FPRkbb5t84');
      const model = genAI.getGenerativeModel({
        model: "gemini-2.0-flash-exp",
        generationConfig: {
          temperature: 1,
          topP: 0.95,
          topK: 40,
          maxOutputTokens: 8192,
        },
        safetySettings: [],
      });

      setProgress({ message: 'Analyzing stocks...', progress: 50 });

      const prompt = `I want to invest in the following stocks: ${stocks.join(', ')}. 
                     I have a capital of ${capital}$ and want to invest for ${duration} months.
                     Please analyze these stocks using current market data and news to provide:
                     # Market Overview
                     Current market status and recent performance

                     # Financial Analysis
                     Key financial metrics and valuation

                     # Recent Developments
                     Recent news and developments

                     # Risk Assessment
                     Detailed risk analysis

                     # Investment Strategy
                     Investment recommendation with allocation strategy
                     
                     Please ensure each section starts with a # followed by the section title.
                     `;

      const aiResponse = await model.generateContent(prompt);
      const analysis = aiResponse.response.text();

      if (!analysis || analysis.trim() === '') {
        throw new Error('Received empty analysis from AI');
      }

      setProgress({ message: 'Processing results...', progress: 90 });
      setResult(analysis);
      setFormattedResult({
        analysis,
        stockData: chartData
      });

      // **Updated API call using local variables**
      const historyResponse = await fetch(`${API_BASE_URL}/api/advisor/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stocks,
          capital,
          duration,
          analysis,
          // **Corrected reference for formattedAnalysis**
          formattedAnalysis: analysis, // or adjust based on backend requirements
          // **Use local chartData instead of stockCharts state**
          stockCharts: chartData,
        }),
      });

      if (!historyResponse.ok) {
        const errorData = await historyResponse.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to store advisory history.');
      }

      const data: AdvisorHistory = await historyResponse.json();

      toast({
        title: 'History Saved',
        description: 'Your advisory analysis has been saved successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to store advisory history.';
      setApiError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // Don't hide the form on error
      setShowInputs(true);
    } finally {
      setIsAnalyzing(false);
      setProgress(null);
    }
  };

  // Define steps for the timeline
  const steps = [
    { title: 'Add Stocks' },
    { title: 'Investment Capital' },
    { title: 'Investment Duration' },
  ];

  const currentStep = useMemo(() => currentStepState - 1, [currentStepState]); // Adjust if necessary

  const renderStepContent = () => {
    switch (currentStepState) {
      case 1:
        return (
          <VStack spacing={4} align="stretch" width="100%">
            <Heading size="md">Step 1: Add Stocks (Max 5)</Heading>
            <HStack>
              <Input
                placeholder="Enter stock symbol (e.g., AAPL)"
                value={currentStock}
                onChange={(e) => setCurrentStock(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddStock()}
              />
              <IconButton
                aria-label="Add stock"
                icon={<AddIcon />}
                onClick={handleAddStock}
                isDisabled={stocks.length >= 5}
              />
            </HStack>
            <Wrap spacing={2}>
              {stocks.map(stock => (
                <WrapItem key={stock}>
                  <Tag
                    size="lg"
                    borderRadius="full"
                    variant="solid"
                    colorScheme="blue"
                  >
                    <TagLabel>{stock}</TagLabel>
                    <TagCloseButton onClick={() => handleRemoveStock(stock)} />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          </VStack>
        );
      case 2:
        return (
          <VStack spacing={4} align="stretch" width="100%">
            <Heading size="md">Step 2: Investment Capital</Heading>
            <NumberInput min={1000} value={capital} onChange={(_, value) => setCapital(value)}>
              <NumberInputField placeholder="Enter amount" />
            </NumberInput>
          </VStack>
        );
      case 3:
        return (
          <VStack spacing={4} align="stretch" width="100%">
            <Heading size="md">Step 3: Investment Duration</Heading>
            <Select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
              {[1, 3, 6, 12].map((month) => (
                <option key={month} value={month}>
                  {month} {month === 1 ? 'Month' : 'Months'}
                </option>
              ))}
            </Select>
          </VStack>
        );
      default:
        return null;
    }
  };

  // Add DataFlow component to represent data exchange between agents
  const DataFlow = () => (
    <motion.svg width="200" height="100" xmlns="http://www.w3.org/2000/svg">
      <motion.line
        x1="50"
        y1="50"
        x2="150"
        y2="50"
        stroke="gray"
        strokeWidth="2"
        initial={{ dasharray: "4 4" }}
        animate={{ dashoffset: [0, -8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      />
      <FaExchangeAlt
        style={{
          position: 'absolute',
          top: '45px',
          left: '90px',
          transform: 'translate(-50%, -50%)',
        }}
        color="gray"
        size={24}
      />
    </motion.svg>
  );

  // Modify the Agent component to include different roles if needed
  const Agent = ({ delay }: { delay: number }) => (
    <motion.div
      initial={{ y: 0 }}
      animate={{ y: [0, -20, 0] }}
      transition={{ repeat: Infinity, duration: 1.5, delay }}
    >
      <FaRobot size={40} color={useColorModeValue('blue.500', 'purple.300')} />
    </motion.div>
  );

  // Replace the existing renderStep function with the timeline
  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <HStack justify="space-between" align="center">
          <Heading textAlign="center" bgGradient="linear(to-r, blue.400, purple.500)" bgClip="text">
            Investment Advisor
          </Heading>
          <Button
            as={Link}
            to="/advisor/history"
            colorScheme="purple"
            variant="outline"
            leftIcon={<TimeIcon />}
          >
            View History
          </Button>
        </HStack>

        {apiError && (
          <Alert status="error" variant="left-accent">
            <AlertIcon />
            <VStack align="start" spacing={2}>
              <Text fontWeight="bold">Analysis Failed</Text>
              <Text>{apiError}</Text>
              <Button size="sm" onClick={() => setApiError(null)}>
                Try Again
              </Button>
            </VStack>
          </Alert>
        )}

        <AnimatePresence>
          {showInputs && (
            <motion.div
              initial={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Box
                p={6}
                bg={bgColor}
                borderRadius="xl"
                boxShadow="xl"
                border="1px"
                borderColor={borderColor}
              >
                <Stepper index={currentStepState - 1} orientation="vertical">
                  {steps.map((step, index) => (
                    <Step key={index}>
                      <StepIndicator>
                        <Box
                          borderWidth="2px"
                          borderColor="gray.300"
                          borderRadius="full"
                          w={6}
                          h={6}
                          bg={index < currentStepState - 1 ? 'green.400' : 'gray.300'}
                        />
                      </StepIndicator>
                      <Box flexShrink="0">
                        <StepTitle>{step.title}</StepTitle>
                        <StepSeparator />
                      </Box>
                    </Step>
                  ))}
                </Stepper>
                <motion.div
                  key={currentStepState}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderStepContent()}
                </motion.div>
                <HStack justify="flex-end" mt={6}>
                  {currentStepState < steps.length ? (
                    <Button
                      colorScheme="blue"
                      onClick={handleNext}
                      isDisabled={stocks.length === 0 && currentStepState === 1}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      colorScheme="green"
                      onClick={handleSubmit}
                      isDisabled={!capital || !duration}
                    >
                      Analyze
                    </Button>
                  )}
                </HStack>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {isAnalyzing && (
          <Box py={10}>
            <AnalysisAgents progress={progress} />
            <Box mt={8} textAlign="center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  bgGradient="linear(to-r, blue.400, purple.500)"
                  bgClip="text"
                >
                  {progress?.message || 'Collaborative Analysis in Progress'}
                </Text>
                <Progress
                  value={progress?.progress || 0}
                  size="lg"
                  colorScheme="blue"
                  hasStripe
                  isAnimated
                  mt={4}
                  borderRadius="full"
                  maxW="600px"
                  mx="auto"
                />
              </motion.div>
            </Box>
          </Box>
        )}

        {formattedResult && !isAnalyzing && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <VStack spacing={8}>
              {(!formattedResult.analysis || formattedResult.analysis.trim() === '') && (
                <VStack spacing={4}>
                  <Text>No analysis content available.</Text>
                  <Button colorScheme="blue" onClick={handleSubmit}>
                    Retry Analysis
                  </Button>
                </VStack>
              )}

              {stockCharts.length > 0 && (
                <SimpleGrid 
                  columns={{ base: 1, lg: 2 }} 
                  spacing={6}
                  w="full"
                  sx={{
                    '& > *:last-child:nth-child(odd)': {
                      gridColumn: { lg: '1 / -1' }
                    }
                  }}
                >
                  {stockCharts.map((chartData) => (
                    <StockChartCard key={chartData.symbol} stockData={chartData} />
                  ))}
                </SimpleGrid>
              )}

              {formattedResult.analysis && (
                <Box
                  p={6}
                  bg={bgColor}
                  borderRadius="xl"
                  boxShadow="xl"
                  border="1px"
                  borderColor={borderColor}
                  w="full"
                >
                  <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between">
                      <Heading size="md">Analysis Result</Heading>
                      <IconButton
                        aria-label="Toggle inputs"
                        icon={showInputs ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        onClick={() => setShowInputs(!showInputs)}
                        variant="ghost"
                      />
                    </HStack>
                    <AnalysisResultView data={formattedResult.analysis} />
                  </VStack>
                </Box>
              )}
            </VStack>
          </motion.div>
        )}
      </VStack>
    </Container>
  );
};
